import { chatOverlay } from './chat-overlay';
import { VIEWS } from './chat-screen-state';

const LAST_NON_CHAT_URL_KEY = 'lastNonChatUrl';

function isChatUrl(url) {
  try {
    return new URL(url).pathname.startsWith('/chat');
  } catch {
    return false;
  }
}
// Call this on every non-chat page load to record where we came from
export function recordNonChatUrl() {
  
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      if (referrerUrl.origin === window.location.origin && !isChatUrl(referrerUrl)) {
        sessionStorage.setItem(LAST_NON_CHAT_URL_KEY, referrerUrl.href);
      }
    } catch { /* Ignore invalid referrers. */ }
  }
}

export function chatFullPage({ currentUserId, canCreatePublicGroup, presencePreference, chatStatus, currentLocale }) {
  // Mutate instead of spread to preserve getters (filteredConversations, activeConversationId)
  const component = chatOverlay({
    currentUserId, canCreatePublicGroup, presencePreference, chatStatus, currentLocale
  });
  recordNonChatUrl();

  // Always open in full-page mode
  component.isOpen = true;
  component.analyticsSection = 'full page';

  // Join prompt state (a URL pointing at a public group the user isn't in)
  component.joinPromptGroup = null;
  component.joinPromptRequesting = false;

  component.joinFromPrompt = async function () {
    const group = this.joinPromptGroup;
    await this.joinGroup(group.id, { source: 'link' });
    if (this.conversations.some((c) => c.id === group.id)) {
      this.joinPromptGroup = null;
    }
  };

  component.requestToJoinFromPrompt = async function () {
    const group = this.joinPromptGroup;
    await this.sendJoinRequest(group.id);
    if (this.pendingGroupIds.has(group.id)) {
      this.joinPromptRequesting = false;
    }
  };

  component.closeJoinPrompt = function () {
    this.joinPromptGroup = null;
    this.joinPromptRequesting = false;
    history.replaceState({}, '', '/chat');
  };

  // Sync URL with the active conversation
  component.onConversationOpened = function () {
    // Keep favorites/hidden lists visible in the sidebar when opening from there
    if (
      this.view !== VIEWS.FAVORITE_MESSAGES
      && this.view !== VIEWS.HIDDEN_MESSAGES
      && this.view !== VIEWS.BLOCKED_CONTACTS
    ) {
      this.view = VIEWS.CONVERSATION;
    }
    const conv = this.getConversation(this.activeConversationId);
    const publicId = conv?.public_id || this.activeConversationId;
    history.pushState({ conversationId: this.activeConversationId }, '', `/chat/conversations/${publicId}`);
  };

  component.onConversationClosed = function () {
    history.pushState({}, '', '/chat');
  };

  component.onFavoriteMessagesOpened = function () {
    history.pushState({}, '', '/chat');
  };

  // Open conversation from URL on load
  const originalInit = component.init;
  component.init = function () {
    originalInit.call(this);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('invalid_conversation_toast')) {
      this.invalidConversationToast = true;
      window.history.replaceState({}, '', '/chat');

      setTimeout(() => {
        this.invalidConversationToast = false;
      }, 4000);
    }

    const match = window.location.pathname.match(/^\/chat\/conversations\/([^/]+)$/);
    if (!match) return;

    const publicId = match[1];
    // Notifications about a single message link to it, so the thread opens
    // scrolled to that message instead of at the bottom.
    const focusMessageId = Number(urlParams.get('message')) || null;
    let urlConversationResolved = false;

    const conversationFromLists = () => (
      this.conversations.find((c) => c.public_id === publicId)
      || this.hiddenConversations.find((c) => c.public_id === publicId)
      || this.blockedConversations.find((c) => c.public_id === publicId)
    );

    const fetchConversationByPublicId = async () => {
      try {
        const response = await this.$api({
          url: `/chat/conversations/${publicId}`,
          method: 'GET'
        });
        const fetched = response.data.conversation;
        this.upsertConversation(fetched);
        return fetched;
      } catch (err) {
        console.error('Failed to fetch conversation from URL:', err);
        return null;
      }
    };

    // Public groups answer with a summary when the user isn't a member;
    // private groups, DMs and deactivated groups are refused.
    const fetchGroupPreview = async () => {
      try {
        const response = await this.$api({
          url: `/chat/conversations/${publicId}/preview`,
          method: 'GET'
        });
        return response.data.conversation;
      } catch {
        return null;
      }
    };

    const openConversationFromUrl = async (conv) => {
      // Blocked DMs are excluded from the hidden list, so blocked_by_me wins
      // when both flags are set — same order as upsertConversation.
      if (conv.blocked_by_me) {
        this.view = VIEWS.BLOCKED_CONTACTS;
        await this.loadBlockedConversations();
        this.upsertConversation(conv);
      } else if (conv.is_hidden) {
        this.view = VIEWS.HIDDEN_MESSAGES;
        await this.loadHiddenConversations();
        this.upsertConversation(conv);
      }
      this.openConversation(conv.id, {
        focusMessageId: conv.blocked_by_me ? null : focusMessageId,
        source: 'url'
      });
    };

    const resolveConversationFromUrl = async () => {
      if (urlConversationResolved || this.isLoading) return;
      urlConversationResolved = true;

      const conv = conversationFromLists() || await fetchConversationByPublicId();
      if (conv) {
        await openConversationFromUrl(conv);
        return;
      }

      const group = await fetchGroupPreview();
      if (group) {
        this.joinPromptGroup = group;
        return;
      }

      window.location.replace('/chat?invalid_conversation_toast=true');
    };

    // Handle both direct load and async load completion
    resolveConversationFromUrl();
    this.$watch('isLoading', resolveConversationFromUrl);
  };

  // Navigate away instead of closing a popup
  component.close = function () {
    this.closeConversation();
    if (this.inboxSubscription) {
      this.inboxSubscription.unsubscribe();
    }
    if (this.presenceSubscription) {
      this.presenceSubscription.unsubscribe();
    }
    // Use the last recorded non-chat URL, fall back to /posts
    const lastNonChatUrl = sessionStorage.getItem(LAST_NON_CHAT_URL_KEY);
    if (lastNonChatUrl) {
      window.location.href = lastNonChatUrl;
    } else {
      window.location.href = '/posts';
    }
  };

  return component;
}
